import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { AuthService, type SocialProvider } from '../../services/auth.service';

/**
 * The Google/GitHub buttons shared by the login and signup pages, with the
 * "or" divider that separates them from the email form.
 *
 * A successful click navigates the browser away to the provider, so this
 * component never renders a success state - failures come back to /login as an
 * `?error=` code, which the login page turns into copy.
 */
@Component({
  selector: 'app-social-sign-in',
  templateUrl: './social-sign-in.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialSignIn {
  private auth = inject(AuthService);

  /** In-app path to land on once the provider round-trip succeeds. */
  readonly redirectPath = input('/dashboard');

  /** Emitted only when the redirect never started (e.g. provider not configured). */
  readonly failed = output<string>();

  protected readonly pending = signal<SocialProvider | null>(null);

  protected async signIn(provider: SocialProvider): Promise<void> {
    if (this.pending()) return;
    this.pending.set(provider);
    const { error } = await this.auth.signInWithProvider(provider, this.redirectPath());
    if (error) {
      this.pending.set(null);
      this.failed.emit(error);
    }
    // On success the browser is already leaving; leave `pending` set so the
    // buttons stay disabled through the navigation.
  }
}
